import api from './api';

const surveyActivationService = {
  getAllOpenSurveysForActivation: async (participantId, bypassCache = true, simulatedDate = null) => {
    console.log(`Calling getAllOpenSurveysForActivation for participant: ${participantId}${simulatedDate ? ', with simulated date: ' + simulatedDate : ''}`);
    
    // Use the exact URL pattern found in Django urls.py
    const endpoint = `/api/participants/${participantId}/all-surveys-for-activation/`;
    
    // Build query parameters for simulated date if provided
    let params = {};
    if (simulatedDate) {
      params.simulated_date = simulatedDate;
    }
    if (bypassCache) {
      params.bypass_cache = 'true';
      // Add a timestamp to ensure fresh data
      params.ts = new Date().getTime();
    }
    
    // Convert params to query string
    const queryString = Object.keys(params).length > 0 
      ? '?' + new URLSearchParams(params).toString() 
      : '';
    
    try {
      console.log(`Making API request to: ${endpoint}${queryString}`);
      const response = await api.get(`${endpoint}${queryString}`);
      console.log('Open surveys API success:', response);
      return response.data;
    } catch (error) {
      console.error(`Open surveys API error for ${endpoint}:`, error.response?.status, error.response?.data || error.message);
      
      // Log debugging info to help troubleshoot
      if (error.response?.status === 404) {
        console.warn(`Endpoint not found: ${endpoint}. Check if the API server is running and the endpoint is registered.`);
        console.warn('This might be due to:');
        console.warn('1. API server not running');
        console.warn('2. Missing or incorrect proxy settings');
        console.warn('3. Endpoint not registered in the server being used');
      }
      
      // Return empty array on error for graceful degradation
      return [];
    }
  },
  
  activateOrDeactivateWindow: async (participantId, windowIds, activate = true) => {
    console.log(`Activating windows for participant: ${participantId}, windowIds: ${JSON.stringify(windowIds)}, activate: ${activate}`);
    
    // Use the exact URL pattern from Django
    const endpoint = `/api/participants/${participantId}/activate-assignments`;
    
    try {
      console.log(`Making activation request to: ${endpoint}`);
      const response = await api.post(endpoint, {
        window_ids: windowIds,
        activate: activate
      });
      console.log('Activation API response:', response);
      return response.data;
    } catch (error) {
      console.error(`Activation API error for ${endpoint}:`, error.response?.status, error.response?.data || error.message);
      return {};
    }
  },
  
  // New function to get all upcoming survey windows, including those in the future
  // This is used specifically for the internal admin view that shows all upcoming windows
  getAllUpcomingSurveyWindows: async (participantId, simulatedDate = null) => {
    console.log(`Fetching all upcoming survey windows for participant: ${participantId}${simulatedDate ? ', with simulated date: ' + simulatedDate : ''}`);
    
    // Use the all-surveys-for-activation endpoint
    const endpoint = `/api/participants/${participantId}/all-surveys-for-activation/`;
    
    // Build query parameters
    let params = {
      bypass_cache: 'true',    // Always bypass cache for this query
      ts: new Date().getTime() // Timestamp to ensure fresh data
    };
    
    if (simulatedDate) {
      params.simulated_date = simulatedDate;
    }
    
    // Convert params to query string
    const queryString = '?' + new URLSearchParams(params).toString();
    
    try {
      console.log(`Making API request to: ${endpoint}${queryString}`);
      const response = await api.get(`${endpoint}${queryString}`);
      console.log('All upcoming survey windows API success:', response);
      
      // We'll handle the future windows filtering on the client side
      // since the backend doesn't support fetching future windows directly
      let windows = response.data || [];
      
      // Make sure we're dealing with an array
      if (!Array.isArray(windows)) {
        // Handle case where response is an object with a structured format
        if (windows.visits) {
          // Extract windows from visits structure
          windows = windows.visits.flatMap(visit => visit.windows || []);
        } else if (windows.windows) {
          windows = windows.windows;
        } else {
          windows = []; // Fallback to empty array if we can't extract windows
        }
      }
      
      // Parse the simulated date for comparison
      let simDate = null;
      if (simulatedDate) {
        // Properly parse the date string format "YYYY-MM-DD HH:MM Timezone" that the simulated date arrives in
        console.log(`Parsing simulated date: ${simulatedDate}`);
        const dt = parseSimulatedDate(simulatedDate)
        
        // Create a proper date object from the simulated date string
        try {
          const parts = simulatedDate.split(' ');
          if (parts.length >= 2) {
            const datePart = parts[0]; // YYYY-MM-DD
            const timePart = parts[1]; // HH:MM
            
            // Construct ISO date string with timezone
            // If timezone is provided, use it, otherwise use UTC
            const timezone = parts.slice(2).join(' ') || 'UTC';
            
            // Convert to ISO string format
            const isoDate = `${datePart}T${timePart}:00.000`;
            console.log(`Constructed ISO date: ${isoDate}, Timezone: ${timezone}`);
            
            // Create date object
            simDate = new Date(isoDate);
            console.log(`Parsed date: ${simDate.toISOString()}`);
            
            // Validate the date
            if (isNaN(simDate.getTime())) {
              console.error('Invalid date created after parsing');
              simDate = new Date(); // Fallback to current date
            }
          } else {
            // Try direct parsing if we can't split by spaces
            simDate = new Date(simulatedDate);
            if (isNaN(simDate.getTime())) {
              console.error('Invalid date after direct parsing');
              simDate = new Date(); // Fallback to current date
            }
          }
        } catch (err) {
          console.error(`Error parsing simulated date: ${err.message}`);
          simDate = new Date(); // Fallback to current date
        }
      } else {
        simDate = new Date(); // Use current date if no simulation
      }
      
      console.log(`Final parsed simulated date: ${simDate.toString()}`);
      console.log(`ISO simulated date: ${simDate.toISOString()}`);
      
      console.log(`Using reference date for window filtering: ${simDate}`);
      
      // Get all windows for the week (7 days) from the reference date
      // This will show the windows scheduled for the entire coming week
      const endDate = new Date(simDate);
      endDate.setDate(endDate.getDate() + 7); // Add 7 days
      
      // Filter to get windows that start on or after the reference date
      // or are already in progress (end time is after reference date)
      const filteredWindows = windows.filter(window => {
        // Parse start and end times
        let startTime = window.start_time ? new Date(window.start_time) : null;
        let endTime = window.end_time ? new Date(window.end_time) : null;
        
        // Skip windows with invalid dates
        if (!startTime || !endTime || isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
          return false;
        }
        
        // Include windows that:
        // 1. Start on or after the reference date but before the end of our week window
        // 2. OR are currently active (start before reference date, end after)
        return (startTime >= simDate && startTime <= endDate) || 
               (startTime <= simDate && endTime >= simDate);
      });
      
      console.log(`Filtered to ${filteredWindows.length} windows in the coming week`);
      return filteredWindows;
    } catch (error) {
      console.error(`Error fetching all upcoming survey windows: ${endpoint}:`, error.response?.status, error.response?.data || error.message);
      return [];
    }
  }
};

export default surveyActivationService;